const {KitModule, registerModule} = window;

class ParticleNetwork extends KitModule {
  constructor($element) {
    super($element);
    this.init();
  }

  init() {
    this.canvas = this.$element;
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) {
      // eslint-disable-next-line no-console
      console.warn('ParticleNetwork: Canvas 2D context is not supported or could not be initialized.');
      return;
    }

    // Find the closest section container to attach mouse events and dimensions to
    this.section = this.canvas.closest('section') || this.canvas.parentElement;

    this.mouse = {x: -9999, y: -9999};
    this.targetMouse = {x: -9999, y: -9999};
    this.nodes = [];
    this.W = 0;
    this.H = 0;

    // Config
    this.MAX_DIST = 150;
    this.MOUSE_DIST = 220;

    this.colors = {
      baseColor: {r: 255, g: 255, b: 255},
      accentColor: {r: 245, g: 200, b: 0},
    };

    this.isPaused = true;
    this.animationFrameId = null;

    this.bindEvents();
    this.resize();
    this.initNodes();

    // Start Intersection Observer
    this.setupIntersectionObserver();
  }

  getMousePos(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width || 1;
    const scaleY = this.canvas.height / rect.height || 1;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  bindEvents() {
    this.lastClientX = -9999;
    this.lastClientY = -9999;

    this.handlePointerMove = (clientX, clientY) => {
      this.lastClientX = clientX;
      this.lastClientY = clientY;
      const r = this.section.getBoundingClientRect();

      // Expand bounds slightly to prevent flickering at the very edge
      if (
        clientX >= r.left - 10 && clientX <= r.right + 10 &&
        clientY >= r.top - 10 && clientY <= r.bottom + 10
      ) {
        const pos = this.getMousePos(clientX, clientY);
        this.targetMouse.x = pos.x;
        this.targetMouse.y = pos.y;

        if (this.mouse.x === -9999) {
          this.mouse.x = this.targetMouse.x;
          this.mouse.y = this.targetMouse.y;
        }
      } else {
        this.targetMouse.x = -9999;
        this.targetMouse.y = -9999;
        this.mouse.x = -9999;
        this.mouse.y = -9999;
      }
    };

    // Store references to the event listener functions so we can cleanly remove them on unmount
    this.onMouseMove = (e) => {
      this.handlePointerMove(e.clientX, e.clientY);
    };

    this.onScroll = () => {
      if (this.lastClientX !== -9999) {
        this.handlePointerMove(this.lastClientX, this.lastClientY);
      }
    };

    window.addEventListener('mousemove', this.onMouseMove);

    window.addEventListener('scroll', this.onScroll, {passive: true});

    this.resizeObserver = new ResizeObserver(() => {
      const r = this.section.getBoundingClientRect();
      if (r.width !== 0 && r.height !== 0) {
        this.resize();
        // If screen drastically changed size, re-calculate density
        const area = this.W * this.H;
        // ~1 particle per 8,000 square pixels for visual density, bound to [30, 180] particles
        const targetCount = Math.max(30, Math.min(180, Math.floor(area / 8000)));
        if (Math.abs(this.COUNT - targetCount) > 20) {
          this.initNodes();
        }
      }
    });
    this.resizeObserver.observe(this.section);
  }

  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.isPaused) {
            this.isPaused = false;
            this.loop();
          }
        } else {
          this.isPaused = true;
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
          }
        }
      });
    }, {threshold: 0});

    this.intersectionObserver.observe(this.section);
  }

  getColors() {
    // Read the current computed CSS variables from the section container to adapt to themes
    const style = window.getComputedStyle(this.section);

    // For primary lines and nodes, we typically use the text color or light/dark inversion
    // The interactive mockup used white nodes on a dark blue background.
    // Let's use --color-text (which is white on dark themes, black on light themes)
    let baseColorStr = style.getPropertyValue('--color-text').trim();
    if (!baseColorStr) baseColorStr = '#ffffff';

    // For accent, the mockup used yellow. In the theme, yellow is often the btn color on dark theme,
    // or blue on light theme. We'll use --color-bg-btn.
    let accentColorStr = style.getPropertyValue('--color-bg-btn').trim();
    if (!accentColorStr) accentColorStr = '#F5C800';

    this.colors = {
      baseColor: this.hexToRgb(baseColorStr, {r: 255, g: 255, b: 255}),
      accentColor: this.hexToRgb(accentColorStr, {r: 245, g: 200, b: 0}),
    };
  }

  hexToRgb(hex, defaultRgb) {
    // simple hex to rgb converter, fallback to default if parsing fails
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    } : defaultRgb;
  }

  resize() {
    const r = this.section.getBoundingClientRect();
    const oldW = this.W;
    const oldH = this.H;

    this.W = this.canvas.width = r.width;
    this.H = this.canvas.height = r.height;

    // Reposition nodes proportionally to prevent clamping at edges when container resizes
    if (oldW && oldH && this.nodes && this.nodes.length > 0) {
      const scaleX = this.W / oldW;
      const scaleY = this.H / oldH;
      this.nodes.forEach((n) => {
        n.x *= scaleX;
        n.y *= scaleY;
      });
    }

    this.getColors();
  }

  initNodes() {
    this.nodes = [];

    // Calculate dynamic density: ~1 particle per 8,000 sq pixels
    // We limit the bounds to [30, 180] particles. This guarantees the background is neither too sparse
    // on small mobile viewports nor too performance-heavy on extremely large desktop screens.
    const area = this.W * this.H;
    this.COUNT = Math.max(30, Math.min(180, Math.floor(area / 8000)));

    for (let i = 0; i < this.COUNT; i++) {
      this.nodes.push({
        x: Math.random() * this.W,
        y: Math.random() * this.H,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        accent: Math.random() < 0.08,
        hollow: Math.random() < 0.25, // Hollow nodes only apply if not accent, calculated in draw
        r: 0, // set in loop based on accent
      });
      // Fix hollow logic to match original script
      const node = this.nodes[i];
      node.r = node.accent ? 3.5 : (Math.random() * 1.4 + 1.2);
      if (node.accent) node.hollow = false;
    }
  }

  updateNode(n) {
    n.x += n.vx;
    n.y += n.vy;

    // Bounds check and snap to prevent getting stuck out of bounds when accordion resizes
    if (n.x < 0) {
      n.x = 0;
      n.vx *= -1;
    } else if (n.x > this.W) {
      n.x = this.W;
      n.vx *= -1;
    }

    if (n.y < 0) {
      n.y = 0;
      n.vy *= -1;
    } else if (n.y > this.H) {
      n.y = this.H;
      n.vy *= -1;
    }
  }

  dist(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  draw() {
    // Lerp mouse
    if (this.targetMouse.x !== -9999) {
      this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.15;
      this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.15;
    }

    const {baseColor, accentColor} = this.colors;

    this.ctx.clearRect(0, 0, this.W, this.H);

    // Draw connections
    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const d = this.dist(this.nodes[i], this.nodes[j]);
        if (d < this.MAX_DIST) {
          const alpha = (1 - d / this.MAX_DIST) * 0.18;
          this.ctx.beginPath();
          this.ctx.moveTo(this.nodes[i].x, this.nodes[i].y);
          this.ctx.setLineDash([4, 5]);
          this.ctx.strokeStyle = `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, ${alpha})`;
          this.ctx.lineWidth = 0.7;
          this.ctx.lineTo(this.nodes[j].x, this.nodes[j].y);
          this.ctx.stroke();
          this.ctx.setLineDash([]);
        }
      }

      // Mouse attraction lines
      const dm = this.dist(this.nodes[i], this.mouse);
      if (dm < this.MOUSE_DIST) {
        const alpha = (1 - dm / this.MOUSE_DIST) * 0.45;
        this.ctx.beginPath();
        this.ctx.moveTo(this.nodes[i].x, this.nodes[i].y);
        this.ctx.setLineDash([]);
        this.ctx.strokeStyle = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, ${alpha})`;
        this.ctx.lineWidth = 0.9;
        this.ctx.lineTo(this.mouse.x, this.mouse.y);
        this.ctx.stroke();
      }
    }

    // Draw nodes
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      const dm = this.dist(n, this.mouse);
      const isLit = dm < this.MOUSE_DIST;

      if (n.accent) {
        this.ctx.beginPath();
        this.ctx.arc(n.x, n.y, isLit ? n.r * 1.6 : n.r, 0, Math.PI * 2);
        this.ctx.fillStyle = isLit ? `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.9)` : `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.55)`;
        this.ctx.fill();
      } else if (n.hollow) {
        this.ctx.beginPath();
        this.ctx.arc(n.x, n.y, isLit ? n.r * 1.8 : n.r + 0.5, 0, Math.PI * 2);
        this.ctx.strokeStyle = isLit ? `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.7)` : `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.3)`;
        this.ctx.lineWidth = isLit ? 1.2 : 0.9;
        this.ctx.stroke();

        this.ctx.beginPath();
        this.ctx.arc(n.x, n.y, 1, 0, Math.PI * 2);
        this.ctx.fillStyle = isLit ? `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.8)` : `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.3)`;
        this.ctx.fill();
      } else {
        this.ctx.beginPath();
        this.ctx.arc(n.x, n.y, isLit ? n.r * 1.6 : n.r, 0, Math.PI * 2);
        this.ctx.fillStyle = isLit ? `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.8)` : `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, 0.2)`;
        this.ctx.fill();
      }
    }

    // Mouse cursor node
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    if (this.mouse.x !== -9999 && !isTouchDevice) {
      this.ctx.beginPath();
      this.ctx.arc(this.mouse.x, this.mouse.y, 5, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.35)`;
      this.ctx.fill();
      this.ctx.beginPath();
      this.ctx.arc(this.mouse.x, this.mouse.y, 2.5, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, 0.7)`;
      this.ctx.fill();

      // Connect mouse to nodes
      for (let i = 0; i < this.nodes.length; i++) {
        const n = this.nodes[i];
        const d = this.dist(this.mouse, n);
        if (d < this.MOUSE_DIST) {
          this.ctx.beginPath();
          this.ctx.moveTo(this.mouse.x, this.mouse.y);
          this.ctx.lineTo(n.x, n.y);
          const alpha = 1 - d / this.MOUSE_DIST;
          this.ctx.strokeStyle = `rgba(${accentColor.r}, ${accentColor.g}, ${accentColor.b}, ${alpha * 0.8})`;
          this.ctx.stroke();
        }
      }
    }
  }

  loop() {
    if (this.isPaused) return;

    this.nodes.forEach((n) => this.updateNode(n));
    this.draw();

    this.animationFrameId = requestAnimationFrame(() => this.loop());
  }

  unmount() {
    // Stop the rendering loop to prevent CPU overhead
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clean up IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Remove window event listeners to prevent memory leaks
    if (this.onMouseMove) {
      window.removeEventListener('mousemove', this.onMouseMove);
    }
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }
}

registerModule('ParticleNetwork', ParticleNetwork);
